import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ALargeSmall,
  Check,
  Copy,
  Palette,
  PenLine,
  Trash2,
  Type,
  type LucideIcon,
} from 'lucide-react';
import { QualityDots } from '@/components/editor/ImagePropertiesBar';
import { useObjectOperations } from '@/hooks/useObjectOperations';
import { useSizeIndicator } from '@/hooks';
import { useKeyboardInset } from './useKeyboardInset';
import {
  DIRECT_SALE_TEXT_TABS,
  type DirectSaleTextTab,
} from './panels/DirectSaleTextPanel';

/**
 * The phone's edit mode: while an object is selected the bottom card steps
 * aside and this bar takes its place — where the thumb is, and where the
 * customer looks after tapping their text. The pills name what can be
 * changed (a sheet only opens once one is picked); the check mark returns to
 * the preview. With the keyboard open the bar rides on top of it.
 *
 * Every action gets its share of the bar by the length of its name, icon
 * over a short label — a
 * row of word pills was wider than a phone and cut off the last ones, and a
 * bar that has to be scrolled hides what it offers.
 *
 * `above` is the view switch (the dots), so the customer can turn the
 * product around while editing, as they can on the preview. It steps aside
 * with the keyboard: the bar then rides on the keyboard and the dots would
 * only take room from the product.
 */
export function DirectSaleMobileEditBar({
  kind,
  onOpenTextTab,
  onDone,
  above,
}: {
  kind: 'text' | 'image';
  onOpenTextTab: (tab: DirectSaleTextTab) => void;
  onDone: () => void;
  above?: ReactNode;
}) {
  const { t } = useTranslation();
  const { duplicateSelected, deleteSelected } = useObjectOperations();
  const { sizeInfo } = useSizeIndicator();
  const keyboardInset = useKeyboardInset(true);
  const quality = kind === 'image' ? (sizeInfo?.quality ?? null) : null;

  const tabLabel: Record<DirectSaleTextTab, string> = {
    write: t('directSale.mobile.tabWrite'),
    font: t('directSale.mobile.fontShort'),
    size: t('directSale.design.size'),
    color: t('directSale.design.color'),
  };
  const tabIcon: Record<DirectSaleTextTab, LucideIcon> = {
    write: PenLine,
    font: Type,
    size: ALargeSmall,
    color: Palette,
  };
  const action =
    'flex h-14 min-w-[44px] flex-auto flex-col items-center justify-center gap-0.5 px-1';
  const caption =
    'max-w-full truncate text-center text-[11px] font-semibold tracking-tight';
  const icon = 'flex h-14 w-10 flex-none items-center justify-center';

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-20 flex flex-col gap-2 px-3 max-[374px]:px-2 pb-[max(env(safe-area-inset-bottom),14px)] pt-2 min-[981px]:hidden"
      style={
        keyboardInset > 0
          ? { transform: `translateY(-${keyboardInset}px)`, paddingBottom: 8 }
          : undefined
      }
    >
      {keyboardInset === 0 && above}
      <div className="flex items-center gap-2 max-[374px]:gap-1.5">
        <div className="flex min-w-0 flex-1 items-stretch rounded-[20px] border border-line bg-paper px-0.5 shadow-[0_2px_8px_rgba(13,13,13,0.08)]">
          {kind === 'text' &&
            DIRECT_SALE_TEXT_TABS.map((tab) => {
              const Icon = tabIcon[tab];
              return (
                <button
                  key={tab}
                  type="button"
                  className={`${action} text-ink`}
                  onClick={() => onOpenTextTab(tab)}
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                  <span className={caption}>{tabLabel[tab]}</span>
                </button>
              );
            })}
          {quality && (
            <div className="flex flex-1 items-center gap-2 px-3">
              <QualityDots level={quality.level} />
              <span className="text-xs" style={{ color: quality.color }}>
                {sizeInfo?.qualityLabel}
              </span>
            </div>
          )}
          {kind === 'image' && !quality && <div className="flex-1" />}
          {/* Copy and delete are read by their icons alone, which leaves the
            text actions room for their full names. */}
          <button
            type="button"
            aria-label={t('directSale.design.duplicate')}
            onClick={() => void duplicateSelected()}
            className={`${icon} text-ink`}
          >
            <Copy className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={t('directSale.design.delete')}
            onClick={deleteSelected}
            className={`${icon} text-danger`}
          >
            <Trash2 className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <button
          type="button"
          aria-label={t('directSale.mobile.done')}
          onClick={onDone}
          className="flex h-14 w-14 flex-none items-center justify-center rounded-full bg-ink max-[374px]:h-12 max-[374px]:w-12 text-white shadow-[0_4px_14px_rgba(13,13,13,0.25)]"
        >
          <Check className="h-[22px] w-[22px]" strokeWidth={2.6} />
        </button>
      </div>
    </div>
  );
}
